import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as communityService from '../services/community';

export const POST_TYPES = {
  ANNOUNCEMENT: 'announcement',
  COMMUNITY: 'community'
};

export const CATEGORIES = {
  GENERAL: 'general',
  QUESTION: 'question',
  DISCUSSION: 'discussion',
  SHOWCASE: 'showcase',
  TIP: 'tip',
  STOCK: 'stock'
};

export const REPORT_STATUS = {
  PENDING: 'pending',
  REVIEWED: 'reviewed',
  DISMISSED: 'dismissed',
  ACTIONED: 'actioned'
};

const CommunityContext = createContext(null);

export const useCommunity = () => {
  const context = useContext(CommunityContext);
  if (!context) {
    throw new Error('useCommunity must be used within CommunityProvider');
  }
  return context;
};

export const CommunityProvider = ({ children }) => {
  const [posts, setPosts] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Backend 데이터 형식을 Frontend 형식으로 변환하는 함수
  const formatPosts = (allPosts) => {
    return allPosts.map(post => ({
      id: post.id,
      type: (post.board === 'announcements' || post.board === 'announcement') ? POST_TYPES.ANNOUNCEMENT : POST_TYPES.COMMUNITY,
      category: post.category || null,
      title: post.title,
      content: post.content,
      author: {
        id: post.author_id,
        name: post.author_name,
        email: post.author_email,
        picture: post.author_picture
      },
      createdAt: post.created_at,
      updatedAt: post.updated_at,
      comments: [],
      attachments: [],
      reportCount: 0,
      isHidden: false
    }));
  };

  // Backend API에서 데이터 로드
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // 공지사항과 커뮤니티 게시글을 병렬로 로드
      const [announcementsRes, communityRes] = await Promise.all([
        communityService.getPosts('announcements', 1, 50),
        communityService.getPosts('free', 1, 50)
      ]);

      const allPosts = [
        ...(announcementsRes.data?.posts || []),
        ...(communityRes.data?.posts || [])
      ];

      setPosts(formatPosts(allPosts));

      // 관리자인 경우 신고 목록도 로드 (에러 무시)
      try {
        const reportsRes = await communityService.getReports();
        setReports(reportsRes.data || []);
      } catch (err) {
        console.log('Reports not loaded (admin only)');
      }
    } catch (err) {
      console.error('Failed to load community data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 게시글 목록 새로고침 (카테고리 탭 전환 등)
  const refreshPosts = useCallback(async () => {
    try {
      const [announcementsRes, communityRes] = await Promise.all([
        communityService.getPosts('announcements', 1, 50),
        communityService.getPosts('free', 1, 50)
      ]);

      const allPosts = [
        ...(announcementsRes.data?.posts || []),
        ...(communityRes.data?.posts || [])
      ];

      setPosts(formatPosts(allPosts));
    } catch (err) {
      console.error('Failed to refresh posts:', err);
    }
  }, []);

  // 공지사항 목록 조회
  const getAnnouncements = useCallback(() => {
    return posts
      .filter(p => p.type === POST_TYPES.ANNOUNCEMENT)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [posts]);

  // 커뮤니티 글 목록 조회 (카테고리 필터 지원)
  // category=null: 전체, category='general': 일반 (null 포함), category='question' 등: 해당 카테고리만
  const getCommunityPosts = useCallback((category = null) => {
    return posts
      .filter(p => p.type === POST_TYPES.COMMUNITY && !p.isHidden)
      .filter(p => {
        if (!category) return true; // 전체
        if (category === CATEGORIES.GENERAL) {
          // "일반" 선택 시: category가 null이거나 'general'인 글 모두 포함
          return !p.category || p.category === CATEGORIES.GENERAL;
        }
        return p.category === category;
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [posts]);

  // 카테고리별 글 수 조회
  const getCategoryCount = useCallback(() => {
    const communityPosts = posts.filter(p => p.type === POST_TYPES.COMMUNITY && !p.isHidden);
    const counts = { all: communityPosts.length };
    Object.values(CATEGORIES).forEach(cat => {
      if (cat === CATEGORIES.GENERAL) {
        // "일반"은 category가 null이거나 'general'인 글 모두 카운트
        counts[cat] = communityPosts.filter(p => !p.category || p.category === CATEGORIES.GENERAL).length;
      } else {
        counts[cat] = communityPosts.filter(p => p.category === cat).length;
      }
    });
    return counts;
  }, [posts]);

  // 글 상세 조회
  const getPostById = useCallback((postId) => {
    return posts.find(p => p.id === postId);
  }, [posts]);

  // 글 작성
  const createPost = useCallback(async (type, title, content, author, category = null, files = []) => {
    try {
      const board = type === POST_TYPES.ANNOUNCEMENT ? 'announcement' : 'free';
      const result = await communityService.createPost(board, title, content, category);

      // 백엔드 응답을 프론트엔드 형식으로 변환
      const formattedPost = formatPosts([result.data])[0];

      // 로컬 상태 업데이트
      setPosts(prev => [formattedPost, ...prev]);
      return formattedPost;
    } catch (error) {
      console.error('Failed to create post:', error);
      throw error;
    }
  }, []);

  // 글 삭제
  const deletePost = useCallback(async (postId, userId, isAdmin) => {
    try {
      await communityService.deletePost(postId);

      // 로컬 상태 업데이트
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch (error) {
      console.error('Failed to delete post:', error);
      throw error;
    }
  }, []);

  // 댓글 작성
  const addComment = useCallback(async (postId, content, author) => {
    try {
      const result = await communityService.createComment(postId, content);

      // 로컬 상태 업데이트
      setPosts(prev => prev.map(p =>
        p.id === postId
          ? { ...p, comments: [...(p.comments || []), result.data] }
          : p
      ));
      return result.data;
    } catch (error) {
      console.error('Failed to add comment:', error);
      throw error;
    }
  }, []);

  // 댓글 삭제
  const deleteComment = useCallback(async (postId, commentId, userId, isAdmin) => {
    try {
      await communityService.deleteComment(commentId);

      // 로컬 상태 업데이트
      setPosts(prev => prev.map(p =>
        p.id === postId
          ? { ...p, comments: p.comments.filter(c => c.id !== commentId) }
          : p
      ));
    } catch (error) {
      console.error('Failed to delete comment:', error);
      throw error;
    }
  }, []);

  // 게시글 신고
  const reportPost = useCallback(async (postId, reason, reporter) => {
    try {
      const result = await communityService.reportPost(postId, reason);

      // 로컬 상태 업데이트
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, reportCount: (p.reportCount || 0) + 1 } : p
      ));

      return result.data;
    } catch (error) {
      console.error('Failed to report post:', error);
      throw error;
    }
  }, []);

  // 댓글 신고
  const reportComment = useCallback(async (postId, commentId, reason, reporter) => {
    try {
      const result = await communityService.reportComment(commentId, reason);
      return result.data;
    } catch (error) {
      console.error('Failed to report comment:', error);
      throw error;
    }
  }, []);

  const value = {
    posts,
    reports,
    loading,
    error,
    getAnnouncements,
    getCommunityPosts,
    getCategoryCount,
    getPostById,
    createPost,
    deletePost,
    addComment,
    deleteComment,
    reportPost,
    reportComment,
    refreshPosts,
    POST_TYPES,
    CATEGORIES,
    REPORT_STATUS
  };

  return (
    <CommunityContext.Provider value={value}>
      {children}
    </CommunityContext.Provider>
  );
};
